import { useState } from 'react';
import { AlertBanner, Avatar, Badge, Button, Dialog, EmptyState, Input, Select, Toast } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type InvitationRow } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate } from '../lib/format.js';

const ROLES = ['OPR', 'LDR', 'MGR', 'AUD', 'FAC'] as const;

/**
 * PENGGUNA & PERAN — Figma `40:384` · M01 · rute `/users` · izin MGR.
 *
 * EMPAT KONDISI PETA §5.7, DAN KENAPA "DIUNDANG BELUM MASUK" BUKAN BARIS YANG SAMA
 * -------------------------------------------------------------------------------
 * Peta menuntut kondisi: aktif · diundang belum masuk · nonaktif · terkunci. Tiga yang
 * pertama adalah `app_user.state`; yang kedua punya TABEL SENDIRI (`invitation`), dan itu
 * bedanya yang penting: sebuah undangan punya kedaluwarsa, dapat dicabut, dan dapat
 * diterbitkan ulang — sementara pengguna tidak. Menyatukan keduanya ke satu tabel akan
 * membuat "cabut undangan" dan "nonaktifkan pengguna" tampak seperti aksi yang sama, dan
 * keduanya sama sekali bukan.
 *
 * Jadi layar ini punya DUA daftar. Yang kedua muncul hanya bila ada undangan yang belum
 * dipakai — sebuah tabel kosong berjudul "Undangan" tidak memberi tahu apa pun.
 *
 * APA YANG BELUM DAPAT DILAKUKAN DARI SINI, DAN ITU DIKATAKAN
 * ---------------------------------------------------------
 * Ubah peran, batasi cakupan area, nonaktifkan, atur ulang kata sandi, dan keluarkan dari
 * semua perangkat semuanya punya rute di kontrak (`PUT /v1/users/{id}/role`,
 * `POST /v1/users/{id}/deactivate`, …) dan belum punya handler — mereka menjawab `501`.
 * Layar ini karena itu MENAMPILKAN peran dan cakupan, dan mengatakan di mana perubahannya
 * belum dapat dilakukan, alih-alih menawarkan tombol yang gagal.
 */
export function Users({ onOpen }: { onOpen: (userId: string) => void }) {
  const { t, locale, translator } = useSession();
  const data = useResource(
    async () => {
      const [users, invitations] = await Promise.all([
        endpoints.users(),
        endpoints.invitations().catch(() => ({ items: [] as InvitationRow[] })),
      ]);
      return { users: users.items, invitations: invitations.items };
    },
    [],
  );

  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<string>('OPR');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'warning'; text: string } | null>(null);

  async function undang() {
    setBusy(true);
    setProblem(null);
    try {
      const result = await endpoints.invite({
        email: email.trim(),
        full_name: fullName.trim(),
        role_code: role as 'OPR',
      });
      /**
       * Keadaan pengiriman DINYATAKAN APA ADANYA (keputusan pemilik proyek nomor 2). Bila
       * transport surel tidak aktif, layar TIDAK berkata "undangan terkirim": ia berkata
       * undangannya tercatat dan penyampaiannya harus dilakukan orang.
       */
      setToast(
        result.delivery_configured
          ? { kind: 'success', text: t('invite.sent', { email: result.email }) }
          : { kind: 'warning', text: t('invite.queued', { email: result.email }) },
      );
      setInviting(false);
      setEmail('');
      setFullName('');
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('users.title')}
      subtitle={t('users.subtitle')}
      resource={data}
      actions={
        <Button variant="primary" onClick={() => setInviting(true)}>
          {t('users.invite')}
        </Button>
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {loaded.users.length === 0 ? (
            <EmptyState
              title={t('users.empty.title')}
              body={t('users.empty.body')}
              action={{ label: t('users.invite'), onClick: () => setInviting(true) }}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('users.column.person')}</th>
                  <th scope="col">{t('users.column.role')}</th>
                  <th scope="col">{t('users.column.employeeNo')}</th>
                  <th scope="col">{t('users.column.state')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <span className="person">
                        <Avatar name={u.full_name ?? '?'} size={28} />
                        <button className="linkish" type="button" onClick={() => onOpen(u.id)}>
                          {u.full_name ?? t('users.nameHidden')}
                        </button>
                      </span>
                    </td>
                    <td>{u.role_code ? t(`role.${u.role_code}`) : '—'}</td>
                    <td>{(u as { employee_no?: string | null }).employee_no ?? '—'}</td>
                    <td>
                      <Badge
                        badgeTone={
                          (u as { state?: string }).state === 'ACTIVE'
                            ? 'SUCCESS'
                            : (u as { state?: string }).state === 'INVITED'
                              ? 'INFORMATION'
                              : 'NEUTRAL'
                        }
                      >
                        {t(`users.state.${(u as { state?: string }).state ?? 'ACTIVE'}`)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {loaded.invitations.length > 0 ? (
            <section className="card">
              <h2 className="card__title">{t('users.invitations.title')}</h2>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('users.column.person')}</th>
                    <th scope="col">{t('users.column.role')}</th>
                    <th scope="col">{t('users.invitations.expires')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.invitations.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.full_name}
                        <br />
                        <span className="card__muted">{i.email}</span>
                      </td>
                      <td>{t(`role.${i.role_code}`)}</td>
                      <td>{formatDate(i.expires_at, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <p className="card__muted">{t('users.changesPending')}</p>

          <Dialog
            open={inviting}
            kind="confirm"
            title={t('invite.title')}
            description={t('users.invite.describe')}
            confirmLabel={t('users.invite')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={!email.includes('@') || fullName.trim().length < 2}
            onConfirm={() => void undang()}
            onCancel={() => setInviting(false)}
          >
            <Input
              label={t('users.field.fullName')}
              value={fullName}
              maxLength={160}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Input
              label={t('users.field.email')}
              inputType="email"
              value={email}
              maxLength={160}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Select
              label={t('users.column.role')}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              options={ROLES.map((r) => ({ value: r, label: t(`role.${r}`) }))}
            />
          </Dialog>

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind={toast.kind}
                signalWord={toast.kind === 'success' ? t('signal.success') : t('signal.warning')}
                title={toast.text}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}

/**
 * DETAIL PENGGUNA — Figma `67:545` · M01 · rute `/users/:id`.
 *
 * Peta §5.7 menuntut empat aksi: ubah peran & cakupan · lihat aktivitas · lihat poin ·
 * keluarkan dari semua perangkat. Dari keempatnya, hanya "lihat poin" punya handler
 * (`GET /v1/point-ledger`); tiga sisanya menjawab `501`. Layar ini karena itu adalah layar
 * BACA yang lengkap dan menyatakan dengan tepat apa yang belum dapat diubah dari sini —
 * yang berguna bagi manajer yang sedang memeriksa seseorang, dan tidak berpura-pura bagi
 * manajer yang ingin mengubah sesuatu.
 */
export function UserDetail({ userId, onBack }: { userId: string; onBack: () => void }) {
  const { t, locale } = useSession();
  const data = useResource(
    async () => {
      const [user, ledger] = await Promise.all([
        endpoints.userDetail(userId),
        endpoints.pointLedger().catch(() => null),
      ]);
      return {
        user,
        points: (ledger?.items ?? []).filter((row) => row.user.id === userId),
      };
    },
    [userId],
  );

  return (
    <Screen
      title={t('userDetail.title')}
      resource={data}
      actions={
        <Button variant="secondary" onClick={onBack}>
          {t('userDetail.back')}
        </Button>
      }
    >
      {(loaded) => (
        <>
          <section className="card">
            <h2 className="card__title">{loaded.user.full_name ?? t('users.nameHidden')}</h2>
            <dl className="facts">
              <div>
                <dt>{t('users.column.role')}</dt>
                <dd>{loaded.user.role_code ? t(`role.${loaded.user.role_code}`) : '—'}</dd>
              </div>
              <div>
                <dt>{t('users.field.email')}</dt>
                <dd>{loaded.user.email ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('users.column.employeeNo')}</dt>
                <dd>{loaded.user.employee_no ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('userDetail.jobTitle')}</dt>
                <dd>{loaded.user.job_title ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('users.column.state')}</dt>
                <dd>
                  <Badge badgeTone={loaded.user.state === 'ACTIVE' ? 'SUCCESS' : 'NEUTRAL'}>
                    {t(`users.state.${loaded.user.state}`)}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt>{t('userDetail.lastLogin')}</dt>
                <dd>
                  {loaded.user.last_login_at
                    ? formatDate(loaded.user.last_login_at, locale)
                    : t('userDetail.neverSignedIn')}
                </dd>
              </div>
            </dl>
          </section>

          <section className="card">
            <h2 className="card__title">{t('userDetail.points')}</h2>
            {loaded.points.length === 0 ? (
              <p className="card__muted">{t('userDetail.points.none')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('points.column.rule')}</th>
                    <th scope="col">{t('points.column.points')}</th>
                    <th scope="col">{t('points.column.state')}</th>
                    <th scope="col">{t('points.column.awardedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.points.map((row) => (
                    <tr key={row.id}>
                      <td>{row.rule_code}</td>
                      <td>{row.points}</td>
                      <td>
                        <Badge badgeTone={row.state === 'FINAL' ? 'SUCCESS' : 'INFORMATION'}>
                          {t(`points.ledgerState.${row.state}`)}
                        </Badge>
                      </td>
                      <td>{formatDate(row.awarded_at, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <p className="card__muted">{t('userDetail.changesPending')}</p>
        </>
      )}
    </Screen>
  );
}
